import { useCallback, useEffect, useState } from 'react';
import type { MonitoringRules, PopulationSource, RollupExclusion } from 'types/spherePoints';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import type { FactorNameSource } from '../../../utils/factorDisplay';
import { decompileSet, type RollupSetModel } from '../RollupSetCard/rollupSetModel';

/** A measure's rollup: its owner-set name (the fields the factor list reads) and its set definition. */
export interface MeasureFactor extends FactorNameSource {
  id: string;
  set: RollupSetModel;
}

export interface UseMeasureFactorsResult {
  factors: MeasureFactor[];
  loading: boolean;
  error: Error | null;
  /** The read answered 403: the caller can discover the measure but not view it, so its factors stay unknown. */
  forbidden: boolean;
  reload: () => void;
}

interface FactorDefinitionResponse {
  id: string;
  factorType?: string | null;
  name?: string;
  metadata?: { name?: string } | null;
  populationSources?: PopulationSource[];
  exclusions?: RollupExclusion[];
  monitoringRules?: MonitoringRules;
}

const isForbidden = (err: unknown): boolean => {
  const e = err as { response?: { status?: number }; status?: number } | null;
  return (e?.response?.status ?? e?.status) === 403;
};

/**
 * The rollup factors a candidate measure defines (`related_spheres` only), read the way
 * `useStatusFactorsState` reads them — `globalSphereCache` carries identity, not factors.
 * A `null` parent never requests.
 */
export function useMeasureFactors(parentId: string | null): UseMeasureFactorsResult {
  const graphClient = useApiClientsOrNull()?.graphClient;
  const [factors, setFactors] = useState<MeasureFactor[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    setFactors([]);
    if (!graphClient || !parentId) {
      setLoading(false);
      setError(null);
      setForbidden(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setForbidden(false);
    graphClient
      .get<{ sphere?: { status?: { factorDefinitions?: FactorDefinitionResponse[] } } }>(
        `/spheres/${parentId}?getStatus=true&getStatusFactors=true&getPerspectives=false&getAssignedPerspectives=false`
      )
      .then((response) => {
        if (cancelled) return;
        const definitions = response?.sphere?.status?.factorDefinitions ?? [];
        setFactors(
          definitions
            .filter((definition) => definition.factorType === 'related_spheres')
            .map((definition) => ({
              id: definition.id,
              name: definition.name,
              metadata: definition.metadata ? { name: definition.metadata.name } : undefined,
              // Pins ride measures_into edges the definition doesn't carry; the sentence only has to tell rollups apart.
              set: decompileSet(definition.populationSources, [], definition.exclusions, definition.monitoringRules),
            }))
        );
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (isForbidden(err)) setForbidden(true);
        else setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [graphClient, parentId, reloadToken]);

  const reload = useCallback(() => setReloadToken((n) => n + 1), []);
  return { factors, loading, error, forbidden, reload };
}
